import { Component, signal, computed, output, inject, OnInit, DestroyRef, LOCALE_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Subject, switchMap, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  PharmacyAutoCompleteItem,
  CreateAdminCartDTO,
} from '../../../../pages/admin-dashboard/cart-management/admin-cart.model';
import { CartService } from '../../../../services/cart/cart';
import { Error as ErrorComponent } from '../../../public-user-component/auth/shared/error/error';

@Component({
  selector: 'app-create-cart-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ErrorComponent],
  templateUrl: './create-cart-dialog.html',
    styleUrl: './create-cart-dialog.scss',
})
export class CreateCartDialogComponent implements OnInit {
  private cartService = inject(CartService);
  private destroyRef = inject(DestroyRef);
  private locale = inject(LOCALE_ID);

  created = output<number>();
  closed = output<void>();

  cartTitle = signal('');
  selectedPharmacy = signal<PharmacyAutoCompleteItem | null>(null);
  pharmacySearchQuery = signal('');

  cartTitleTouched = signal(false);
  pharmacyTouched = signal(false);

  cartTitleError = computed(() => {
    if (!this.cartTitleTouched()) return null;
    if (this.cartTitle().length > 150) return $localize`:@@cartTitleMaxError:Cart title cannot exceed 150 characters`;
    if (this.selectedPharmacy()!.isSuspended) {
      return this.isArabic() ? 'هذه الصيدلية متوقفة عن الطلب' : 'This pharmacy is suspended from ordering';
    }
    return null;
  });

  pharmacyError = computed(() => {
    if (!this.pharmacyTouched()) return null;
    if (this.pharmacySearchQuery() && !this.selectedPharmacy()) return null;
    if (!this.selectedPharmacy()) return $localize`:@@pharmacyRequiredError:Please select a pharmacy.`;
    return null;
  });

  isArabic(): boolean {
    return this.locale.startsWith('ar');
  }

  pharmacyResults = signal<PharmacyAutoCompleteItem[]>([]);
  showDropdown = signal(false);
  pharmacyLoading = signal(false);
  noResults = signal(false);

  loading = signal(false);
  apiError = signal<string | null>(null);

  private searchSubject = new Subject<string>();

  isFormValid = computed(
    () => this.selectedPharmacy() !== null && !this.selectedPharmacy()!.isSuspended && this.cartTitle().length <= 150,
  );
  ngOnInit(): void {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          if (!query || query.length < 2) {
            this.pharmacyResults.set([]);
            this.showDropdown.set(false);
            this.noResults.set(false);
            return of(null);
          }
          this.pharmacyLoading.set(true);
          return this.cartService.getPharmacyAutoComplete({ quickSearch: query, isApproved: 1 }).pipe(
            catchError(() => {
              this.pharmacyLoading.set(false);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.pharmacyLoading.set(false);
        if (result) {
          this.pharmacyResults.set(result.list);
          this.noResults.set(result.list.length === 0);
          this.showDropdown.set(true);
        }
      });
  }

  onPharmacyInputChange(value: string): void {
    this.pharmacySearchQuery.set(value);
    this.pharmacyTouched.set(true);
    if (!value) {
      this.clearPharmacySelection();
      return;
    }
    if (this.selectedPharmacy()?.nickName !== value) this.selectedPharmacy.set(null);
    this.searchSubject.next(value);
  }

  selectPharmacy(pharmacy: PharmacyAutoCompleteItem, event: Event): void {
    event.stopPropagation();
    this.selectedPharmacy.set(pharmacy);
    this.pharmacySearchQuery.set(pharmacy.nickName);
    this.showDropdown.set(false);
    this.pharmacyResults.set([]);
  }

  clearPharmacySelection(): void {
    this.selectedPharmacy.set(null);
    this.pharmacySearchQuery.set('');
    this.pharmacyResults.set([]);
    this.showDropdown.set(false);
  }

  hideDropdown(): void {
    setTimeout(() => this.showDropdown.set(false), 200);
  }

  confirm(): void {
    this.pharmacyTouched.set(true);
    this.cartTitleTouched.set(true);

    if (!this.isFormValid() || this.loading()) return;

    const dto: CreateAdminCartDTO = {
      pharmacyId: this.selectedPharmacy()!.id,
      title: this.cartTitle().trim() || null,
    };

    this.loading.set(true);
    this.apiError.set(null);

    this.cartService
      .createAdminCart(dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.loading.set(false);
          this.created.emit(res.id);
          this.resetForm();
        },
        error: (err) => {
          this.loading.set(false);
          let errorMessage = '';
          if (err.reqBodyErrors && err.reqBodyErrors.length > 0) {
            errorMessage = err.reqBodyErrors.join(' - ');
          } else {
            const isAr = this.locale.startsWith('ar');
            errorMessage = isAr ? err.messageAr || err.messageEn : err.messageEn || err.messageAr;
          }
          this.apiError.set(errorMessage || 'An error occurred');
        },
      });
  }

  cancel(): void {
    this.resetForm();
    this.closed.emit();
  }

  private resetForm(): void {
    this.cartTitle.set('');
    this.selectedPharmacy.set(null);
    this.pharmacySearchQuery.set('');
    this.apiError.set(null);
    this.cartTitleTouched.set(false);
    this.pharmacyTouched.set(false);
    this.loading.set(false);
  }
}
